import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { searchResearchStocks } from "@/api/research";
import { addToWatchlist, removeFromWatchlist, getRecommendStocks } from "@/api/market";
import type { WatchlistEntry } from "@/api/market";
import { listSessions } from "@/api/alpha";
import { useSubscription } from "@/hooks/useSubscription";
import { useRealtimeWatchlist } from "@/hooks/useRealtimeWatchlist";
import PaywallModal from "@/components/feature/PaywallModal";

function stripPrefix(code: string): string {
  const c = code.trim().toLowerCase();
  return (c.startsWith("sh") || c.startsWith("sz") || c.startsWith("bj")) ? c.slice(2) : c;
}

function addPrefix(code: string): string {
  const c = code.trim();
  if (c.startsWith("sh") || c.startsWith("sz") || c.startsWith("bj")) return c;
  if (c.length === 6) {
    const head = c[0];
    if (head === '4' || head === '8' || head === '9') return "bj" + c;
    return ('6' === head || '5' === head) ? "sh" + c : "sz" + c;
  }
  return c;
}

export default function WatchlistPage() {
  const navigate = useNavigate();
  const sub = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const [tab, setTab] = useState<"ai" | "user">("ai");
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [watchlistCodes, setWatchlistCodes] = useState("");
  const [aiStocks, setAiStocks] = useState<any[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { watchlist, loading: wlLoading, refresh: refreshWatchlist } = useRealtimeWatchlist();

  // Load session for AI recommendations
  useEffect(() => {
    listSessions().then(({ sessions }) => {
      const s = sessions.find((x: any) => x.status === "running") || sessions[0];
      if (s) setSessionId(s.id);
    }).catch(() => {});
  }, []);

  // Sync watchlist codes for AI recommendations
  useEffect(() => {
    const codes = watchlist.map((w: any) => stripPrefix(w.code)).join(",");
    setWatchlistCodes(codes);
  }, [watchlist]);

  // Cache AI recommendations by sessionId — no re-fetch on tab switches
  const aiCache = useRef<Record<number, any[]>>({});
  useEffect(() => {
    if (tab !== "ai" || !sessionId) return;
    if (aiCache.current[sessionId]) {
      setAiStocks(aiCache.current[sessionId]);
      return;
    }
    setAiLoading(true);
    getRecommendStocks(sessionId, watchlistCodes || undefined)
      .then((res: any) => {
        const stocks = res?.stocks || res?.data || [];
        aiCache.current[sessionId] = stocks;
        setAiStocks(stocks);
        setAiLoading(false);
      })
      .catch(() => { setAiLoading(false); setAiStocks([]); });
  }, [tab, sessionId, watchlistCodes]);

  // Close search suggestions on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const doSearch = (q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchResearchStocks(q.trim());
        setSuggestions(res.list || []);
        setShowSuggestions(res.list?.length > 0);
        setSelectedIdx(-1);
      } catch { setSuggestions([]); setShowSuggestions(false); }
      finally { setSearching(false); }
    }, 300);
  };

  const addStock = async (stock: any) => {
    if (!sub.subscribed) { setShowPaywall(true); return; }
    const code = stock.code || stock.stockCode || "";
    if (!code) return;
    const prefixed = addPrefix(code);
    // Check if already in watchlist
    if (watchlist.some((w) => stripPrefix(w.code) === stripPrefix(prefixed))) return;
    try {
      await addToWatchlist(prefixed);
      setSearch("");
      setShowSuggestions(false);
      setShowAdd(false);
      refreshWatchlist();
    } catch { /* ignore */ }
  };

  const removeStock = async (code: string) => {
    if (!sub.subscribed) { setShowPaywall(true); return; }
    const prefixed = addPrefix(stripPrefix(code));
    try {
      await removeFromWatchlist(prefixed);
      refreshWatchlist();
    } catch { /* ignore */ }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx((p) => Math.min(p + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx((p) => Math.max(p - 1, 0)); }
    else if (e.key === "Enter" && selectedIdx >= 0) { e.preventDefault(); addStock(suggestions[selectedIdx]); }
    else if (e.key === "Escape") { setShowSuggestions(false); }
  };

  const renderPrice = (code: string, entry: WatchlistEntry) => {
    const price = entry.currentPrice ?? 0;
    const change = entry.todayChangePct ?? entry.changePct ?? 0;
    return { price, change };
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-s-line text-foreground-600 text-lg" /></button>
          <h1 className="text-lg font-bold text-foreground-950">自选</h1>
        </div>
        {tab === "user" && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 text-white text-[11px] font-medium cursor-pointer">
            <i className="ri-add-line text-xs" /> 添加
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-4 mb-3">
        <button onClick={() => setTab("ai")}
          className={`text-[11px] px-3 py-1 rounded-full font-medium cursor-pointer ${tab === "ai" ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>AI自选</button>
        <button onClick={() => setTab("user")}
          className={`text-[11px] px-3 py-1 rounded-full font-medium cursor-pointer ${tab === "user" ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>我的自选</button>
      </div>

      {/* AI tab */}
      {tab === "ai" && (
        <div className="flex-1 px-4 space-y-2">
          {aiLoading ? (
            <div className="flex items-center justify-center py-12"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : aiStocks.length === 0 ? (
            <div className="flex items-center justify-center py-12"><p className="text-xs text-foreground-400">暂无 AI 推荐股票</p></div>
          ) : (
            aiStocks.slice(0, 8).map((s: any) => {
              const code = s.code || s.stockCode || "";
              const price = s.price;
              const changePct = s.changePct;
              const loaded = price !== undefined && price !== null && price > 0;
              const score = s.score || 0;
              const rating = score >= 0.85 ? { label: "超配", color: "#E11D48" } : score >= 0.75 ? { label: "增持", color: "#F97316" } : score >= 0.65 ? { label: "中性", color: "#64748B" } : { label: "关注", color: "#3B82F6" };
              return (
                <div key={code} onClick={() => navigate(`/stock/${code}`)}
                  className="bg-white rounded-xl p-4 border border-background-200/60 flex items-center justify-between cursor-pointer hover:border-primary-200 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="text-sm font-semibold text-foreground-950">{s.name || "--"}</span><span className="text-xs text-foreground-400">{code}</span></div>
                    <div className="text-[10px] text-foreground-500 mt-0.5">{s.reason || s.industry || ""}</div>
                  </div>
                  <div className="text-right mr-3">
                    <div className="text-sm font-semibold text-foreground-950">{loaded ? `¥${price.toFixed(2)}` : "--"}</div>
                    <div className="text-[10px] font-medium">
                      {loaded ? (
                        <span className={(changePct ?? 0) >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}>
                          {(changePct ?? 0) >= 0 ? "+" : ""}{(changePct ?? 0).toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-foreground-300">--</span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ color: rating.color, background: `${rating.color}15` }}>
                    {loaded ? rating.label : "--"}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* User watchlist tab */}
      {tab === "user" && (
        <div className="flex-1 px-4 space-y-2">
          {wlLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : watchlist.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-star-line text-3xl text-foreground-300" />
              <p className="text-sm text-foreground-500 mt-3">还没有自选股</p>
              <p className="text-xs text-foreground-400 mt-1">点击右上角「添加」开始</p>
            </div>
          ) : (
            watchlist.map((w) => {
              const { price, change } = renderPrice(w.code, w);
              const tagLabel = change >= 0 ? "关注" : "审慎";
              const tagColor = change >= 0 ? "#E11D48" : "#10B981";
              const bareCode = stripPrefix(w.code);
              return (
                <div key={w.code} onClick={() => navigate(`/stock/${bareCode}`)}
                  className="bg-white rounded-xl p-4 border border-background-200/60 flex items-center justify-between cursor-pointer hover:border-primary-200 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-950">{w.name}</span>
                      <span className="text-xs text-foreground-400 font-mono">{bareCode}</span>
                    </div>
                    <div className="text-[10px] text-foreground-500 mt-0.5">
                      {w.industry ? `市值${(price * /* rough cap placeholder */ 1).toFixed(0)}亿` : "自选股"}
                    </div>
                  </div>
                  <div className="text-right mr-3">
                    <div className="text-sm font-semibold text-foreground-950">¥{price.toFixed(2)}</div>
                    <div className={`text-[10px] font-medium ${change >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                      {change >= 0 ? "+" : ""}{change.toFixed(2)}%
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap" style={{ color: tagColor, background: `${tagColor}15` }}>
                    {tagLabel}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); removeStock(w.code); }}
                    className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-500 cursor-pointer hover:bg-red-100 transition-colors ml-2 whitespace-nowrap">
                    删除
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Add stock modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white w-full max-w-sm rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950">添加自选</h2>
              <button onClick={() => setShowAdd(false)} className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-foreground-500 text-sm" /></button>
            </div>
            <div className="relative" ref={searchRef}>
              <input value={search}
                onChange={(e) => { setSearch(e.target.value); doSearch(e.target.value); }}
                onKeyDown={handleKeyDown}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                placeholder="输入股票代码或名称搜索"
                autoFocus
                className="w-full bg-background-100 rounded-xl px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none border-none" />
              {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-background-200/60 shadow-lg z-50 max-h-[240px] overflow-y-auto">
                  {suggestions.map((s, i) => (
                    <button key={s.code || i}
                      onMouseDown={() => addStock(s)}
                      onMouseEnter={() => setSelectedIdx(i)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${i === selectedIdx ? "bg-primary-50" : "hover:bg-background-50"} ${i === 0 ? "rounded-t-xl" : ""} ${i === suggestions.length - 1 ? "rounded-b-xl" : ""}`}>
                      <span className="text-sm font-semibold text-foreground-950">{s.name || s.stockName}</span>
                      <span className="text-[10px] text-foreground-400 font-mono">{s.code || s.stockCode}</span>
                      {s.industry && <span className="text-[9px] text-foreground-300 ml-auto">{s.industry}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {watchlist.length > 0 && (
              <div className="mt-4 text-[10px] text-foreground-400">
                已添加 {watchlist.length} 只自选股
              </div>
            )}
          </div>
        </div>
      )}
      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} title="" description="自选股功能需要联系客服开通" />
    </div>
  );
}
